import React, { useState } from "react";
import { animated, Spring } from "react-spring";
import { alpha, Box, makeStyles, Typography, useTheme } from "@material-ui/core";
import clsx from "clsx";

import { ExpandLess as ExpandLessIcon } from "@eroad/icons/material";
import { Close } from "@eroad/icons/svg";

import type { CoretexTheme } from "../theme";
import type { AlertItem } from "./types";

const AnimatedExpandLessIcon = animated(ExpandLessIcon);

const animationPaperConfig = {
  mass: 2,
  friction: 1,
  tension: 300,
  clamp: true,
};

const useStyles = makeStyles((theme: CoretexTheme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    minHeight: 20,
    marginBottom: 4,
  },
  rail: {
    width: 4,
    minHeight: 20,
    alignSelf: "stretch",
  },
  alertsContainer: {
    flexDirection: "column",
    width: "100%",
  },
  alerts: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 2),
  },
  header: {
    fontWeight: theme.typography.fontWeightMedium as number,
    color: theme.palette.mono.primaryDark,
    fontSize: 12,
    cursor: "pointer",
  },
  desc: {
    fontWeight: theme.typography.fontWeightMedium as number,
    color: theme.palette.mono.primaryDark,
    fontSize: 12,
  },
  underlineHover: {
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  close: {
    height: 8,
    width: 8,
    cursor: "pointer",
    marginLeft: "auto",
    color: theme.palette.mono.primaryDark,
  },
  expandIcon: {
    height: 16,
    width: 16,
    marginRight: 8,
    color: theme.palette.mono.primaryDark,
    cursor: "pointer",
  },
  count: {
    marginLeft: "auto",
    fontWeight: theme.typography.fontWeightBold as number,
    fontSize: 12,
    color: theme.palette.mono.primaryDark,
  },
  action: {
    marginLeft: "auto",
    fontSize: 12,
    color: theme.palette.colour.primaryBlue,
    cursor: "pointer",
    fontWeight: theme.typography.fontWeightMedium as number,
  },
}));

export interface VehicleAlertsProps<D extends AlertItem> {
  alerts: D[];
  alertsGroupName: string;
  clearAlertsMessage?: string;
  showAlerts?: boolean;
  colour?: React.CSSProperties["color"];
  onClear?: (key: string | number) => void;
  onClearAll?: () => void;
  onAlertClick?: (key: string | number) => void;
  onToggleExpand?: (isExpanded: boolean) => void;
}

const VehicleAlerts = <D extends AlertItem>({
  alerts,
  alertsGroupName,
  clearAlertsMessage = "Clear",
  showAlerts = false,
  colour,
  onClear,
  onClearAll,
  onAlertClick,
  onToggleExpand,
}: VehicleAlertsProps<D>): React.ReactElement => {
  const classes = useStyles();
  const theme = useTheme<CoretexTheme>();
  const [expand, setExpand] = useState(showAlerts);

  React.useEffect(() => {
    setExpand(showAlerts);
  }, [showAlerts]);

  if (!alerts.length) return <></>;

  const singleAlert = (): React.ReactElement => (
    <Box data-testid="single-alert" className={classes.alertsContainer}>
      {alerts.map((alert) => (
        <Box className={classes.alerts} key={alert.key}>
          <Typography
            className={clsx(classes.desc, onAlertClick && classes.underlineHover)}
            onClick={() => {
              if (onAlertClick) onAlertClick(alert.key);
            }}
          >
            {alert.description}
          </Typography>
          <Close
            className={classes.close}
            onClick={() => {
              if (onClear) onClear(alert.key);
            }}
          />
        </Box>
      ))}
    </Box>
  );

  const expandedMultipleAlerts = (): React.ReactElement => (
    <Box data-testid="expanded-alerts">
      {alerts.map((alert) => (
        <Box className={classes.alerts} key={alert.key}>
          <Typography
            className={clsx(classes.desc, onAlertClick && classes.underlineHover)}
            onClick={() => {
              if (onAlertClick) onAlertClick(alert.key);
            }}
          >
            {alert.description}
          </Typography>
          <Close
            className={classes.close}
            onClick={() => {
              if (onClear) onClear(alert.key);
            }}
          />
        </Box>
      ))}
    </Box>
  );

  const renderAlert = (): React.ReactElement => {
    if (alerts.length === 1) {
      return singleAlert();
    }
    return (
      <Box data-testid="alerts-container" className={classes.alertsContainer}>
        <Box className={classes.alerts}>
          <Box
            data-testid="alerts-group-header"
            style={{ display: "flex" }}
            onClick={() => {
              const newState = !expand;
              if (onToggleExpand) onToggleExpand(newState);
              setExpand(newState);
            }}
          >
            <Spring config={animationPaperConfig} to={{ transform: expand ? "rotate(180deg)" : "rotate(90deg)" }}>
              {(style) => <AnimatedExpandLessIcon className={classes.expandIcon} style={style} />}
            </Spring>
            <Typography className={classes.header}>{alertsGroupName}</Typography>
          </Box>
          {expand ? (
            <Typography
              data-testid="clear-all-alerts-button"
              className={classes.action}
              onClick={() => {
                if (onClearAll) onClearAll();
              }}
            >
              {clearAlertsMessage}
            </Typography>
          ) : (
            <Typography className={classes.count}>{alerts.length}</Typography>
          )}
        </Box>
        {expand && expandedMultipleAlerts()}
      </Box>
    );
  };

  return (
    <Box
      data-testid="vehicle-alerts"
      className={classes.root}
      style={{
        backgroundColor: alpha(colour ?? theme.palette.mono.primaryDark ?? "", 0.05),
      }}
    >
      <Box
        className={classes.rail}
        style={{
          backgroundColor: colour ?? theme.palette.mono.primaryDark ?? "",
        }}
      />
      {renderAlert()}
    </Box>
  );
};

export default VehicleAlerts;
